import { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "@tellescope/ui";
import { useConfirmStripePayment } from "../useConfirmStripePayment";

interface ShopPaymentElementFormProps {
  businessName: string;
  onConfirmed: () => void;
}

/**
 * Payment Element form for the Stripe Connect checkout path (prepare_stripe_checkout's clientSecret
 * is a PaymentIntent here, not an embedded Checkout Session — see PaymentSummary's isCheckout branch).
 * Always confirms immediately (the shop has no save-for-later flow), unlike the more general
 * StripePaymentForm in packages/apis/forms, which also supports a setup-intent/save path.
 */
export const ShopPaymentElementForm = ({ businessName, onConfirmed }: ShopPaymentElementFormProps) => {
  const { t } = useTranslation("shop");
  const stripe = useStripe();
  const elements = useElements();

  const confirmPaymentMutation = useConfirmStripePayment(stripe, elements, onConfirmed);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    confirmPaymentMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ business: { name: businessName } }} />
      <Button type="submit" className="w-full" size="lg" loading={confirmPaymentMutation.isPending} disabled={!stripe}>
        {t("Place Order")}
      </Button>
      {confirmPaymentMutation.error && (
        <p className="text-sm text-destructive text-center">{confirmPaymentMutation.error.message}</p>
      )}
    </form>
  );
};
